/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-04-10 11:01:30
 */
import './index.scss'
import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react'
import type { EChartsType } from 'echarts'
import * as echarts from 'echarts'
import FilterControls from '../FilterControls'
import { getPersonStat } from '../../../../actions/dataStatistics'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { Spin, Empty } from 'antd'
import dayjs from 'dayjs'

// 人员数据类型
interface PersonData {
  userID: string
  userName: string
  photoCnt: number
  [key: string]: unknown
}

// 水印类型
interface WatermarkItem {
  watermarkBaseID: string
  groupWatermarkID: string
}

// 过滤器类型
interface PersonFilter {
  times: {
    start: string
    end: string
  }
  member_id: string
  departmentUsers: unknown[]
  watermarks?: WatermarkItem[]
  [key: string]: unknown
}

// 新过滤器类型
interface NewFilter {
  filter?: Record<string, unknown>
  [key: string]: unknown
}

// Props 类型
interface PersonStatProps {
  handleClickReportSA: (clickItem: string, params?: Record<string, unknown>) => void
}

// 暴露的 ref 方法类型
export interface PersonStatRef {
  refreshData: () => Promise<{ success: boolean; message?: string; error?: unknown }>
}

const PersonStat = forwardRef<PersonStatRef, PersonStatProps>(
  ({ handleClickReportSA }, ref) => {
    // 使用 hooks 替代 connect
    const pickerUserCount = useAppSelector((state) => state.organization.pickerOrgUserCount)
    const chartRef = useRef<HTMLDivElement>(null)
    const dispatch = useAppDispatch()
    const [loading, setLoading] = useState(false)
    const [peoples, setPeoples] = useState<PersonData[]>([])
    const [newFilter, setNewFilter] = useState<NewFilter>({})

    // 添加组件挂载状态跟踪
    const isMounted = useRef(true)

    const [personFilter, setPersonFilter] = useState<PersonFilter>({
      times: {
        start: dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        end: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
      },
      member_id: '',
      departmentUsers: []
    })

    // 组件卸载时更新挂载状态
    useEffect(() => {
      return () => {
        isMounted.current = false
      }
    }, [])

    useEffect(() => {
      let chart: EChartsType | null = null

      if (chartRef.current && peoples.length > 0) {
        // 初始化前检查是否有已存在的图表实例
        const existingInstance = echarts.getInstanceByDom(chartRef.current)
        if (existingInstance) {
          echarts.dispose(chartRef.current)
        }

        try {
          const sortedPeoples = [...peoples].sort(
            (a, b) => b.photoCnt - a.photoCnt
          )
          chart = echarts.init(chartRef.current)

          const option = {
            grid: {
              left: '24px',
              right: '24px',
              bottom: '30px',
              containLabel: true
            },
            tooltip: {
              trigger: 'axis',
              axisPointer: {
                type: 'shadow'
              },
              formatter: (params: { dataIndex: number }[]) => {
                const person = params[0].dataIndex
                return `姓名：${sortedPeoples[person].userName}<br/>拍照数量：${sortedPeoples[person].photoCnt} 张`
              }
            },
            xAxis: {
              type: 'category',
              data: sortedPeoples.map((person) => {
                // 超过4个字符就换行
                const name = person.userName
                return name.length > 4
                  ? name.slice(0, 4) + '\n' + name.slice(4)
                  : name
              }),
              axisLabel: {
                interval: 0,
                lineHeight: 18,
                formatter: (value) => value
              }
            },
            yAxis: {
              type: 'value'
            },
            series: [
              {
                name: '拍照数量',
                data: sortedPeoples.map((person) => person.photoCnt),
                type: 'bar',
                barWidth: '30%',
                barMaxWidth: 50,
                itemStyle: {
                  color: '#407AFF'
                }
              }
            ],
            legend: {
              data: ['拍照数量'],
              left: '20px',
              top: '15px',
              itemWidth: 10,
              itemHeight: 10,
              textStyle: {
                color: '#646A73',
                fontSize: '12px'
              }
            }
          }
          chart.setOption(option)

          chart.on('click', (params: { dataIndex: number }) => {
            const { dataIndex: personIndex } = params
            const { userID } = sortedPeoples[personIndex]
            const { start, end } = personFilter.times
            const formattedWatermarks =
              personFilter.watermarks && Array.isArray(personFilter.watermarks)
                ? personFilter.watermarks.map(
                    ({ watermarkBaseID, groupWatermarkID }: WatermarkItem) =>
                      `${watermarkBaseID}+${groupWatermarkID}`
                  )
                : []

            sessionStorage.setItem(
              'watermarks',
              JSON.stringify(formattedWatermarks)
            )

            handlePersonStatClickReportSA('column', {
              rankNum: personIndex + 1,
              name: sortedPeoples[personIndex].userName
            })

            const url = `/photo/all?form_place=data_statistics_member&member_id=${userID}&start_time=${encodeURIComponent(
              start
            )}&end_time=${encodeURIComponent(end)}&has_watermark=true`
            window.open(url)
          })

          // 添加窗口大小变化监听
          const handleResize = () => {
            chart && chart.resize()
          }
          window.addEventListener('resize', handleResize)

          // 在清理函数中移除事件监听和销毁图表
          return () => {
            window.removeEventListener('resize', handleResize)
            if (chart) {
              try {
                chart.dispose()
              } catch (e) {
                console.error('Failed to dispose chart:', e)
              }
            }
          }
        } catch (error) {
          console.error('Error initializing chart:', error)
        }
      }

      // 如果没有创建图表，也返回一个空的清理函数
      return () => {}
    }, [peoples, personFilter])

    const handleFilterChange = async (newFilter: NewFilter) => {
      // 确保组件仍然挂载
      if (!isMounted.current) return { success: false }

      try {
        // 删除 newFilter 里面的 filter 中的 departments 和 departmentUsers
        // 支持普通对象
        const filterData = { ...newFilter }
        if (filterData.filter) {
          const { departments, departmentUsers, ...restFilter } = filterData.filter
          filterData.filter = restFilter
        }

        if (isMounted.current) {
          setNewFilter(filterData)
          setPersonFilter((prevFilter) => ({
            ...prevFilter,
            ...filterData.filter
          }))
          setLoading(true)
        }

        const res = await dispatch(getPersonStat(filterData))

        if (isMounted.current) {
          setPeoples(Array.isArray(res.peoples) ? res.peoples.slice(0, 20) : [])
          setLoading(false)
        }

        return { success: true }
      } catch (error) {
        console.error('Failed to fetch person stat:', error)
        if (isMounted.current) {
          setLoading(false)
        }
        return { success: false, error }
      }
    }

    const handlePersonStatClickReportSA = (clickItem: string, params?: Record<string, unknown>) => {
      handleClickReportSA(clickItem, {
        themeType: 'staffManage',
        ...params
      })
    }

    // 暴露刷新方法
    useImperativeHandle(ref, () => ({
      refreshData: () => {
        if (!newFilter || Object.keys(newFilter).length === 0) {
          return Promise.resolve({
            success: false,
            message: '没有可用的筛选条件'
          })
        }
        return handleFilterChange(newFilter)
      }
    }))

    return (
      <div
        className="main-content-container"
        style={{ padding: '0', minHeight: '400px' }}
      >
        <Spin spinning={loading}>
          <FilterControls
            pickerUserCount={pickerUserCount}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onFilterChange={handleFilterChange as any}
            handleClickReportSA={handlePersonStatClickReportSA}
            title="人员统计"
          />
          <div
            ref={chartRef}
            style={{
              width: '100%',
              height: '400px',
              display: peoples.length > 0 ? 'block' : 'none'
            }}
          ></div>
          {peoples.length === 0 && (
            <div
              style={{
                width: '100%',
                height: '400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Empty description="暂无数据" />
            </div>
          )}
        </Spin>
      </div>
    )
  }
)

export default PersonStat
